import { useCallback, useContext, useEffect, useMemo, useState } from 'react'
import moment from 'moment'
import type { PageBannerCarouselItem } from 'LuxKit/Banners/types'
import CookiesContext from 'contexts/CookiesContext'
import {
  dismissedBannerIdsFromCookie,
  LAB_V2_HEADER_BANNER_DISMISSALS_COOKIE,
  recordLabV2HeaderBannerDismissal,
} from 'cookies/labV2HeaderBannerDismissals'
import useIsOfferPage from 'hooks/Navigation/useIsOfferPage'
import useIsSearchPage from 'hooks/Navigation/useIsSearchPage'
import { useLabRouteRequestContext } from 'hooks/useLabRouteRequestContext'
import { useAppSelector } from 'hooks/reduxHooks'
import { headerBannerDismissalId, headerBannersForPage, headerBannersFromUis } from 'lib/labV2/headerBanners'
import { useShouldShowBanner } from 'takeovers/utils/hooks/useShouldShowBanner'
import { getTakeoverBannerLocation } from './NotificationBannerV3.utils'

import { labV2HeaderBannerCarouselItem } from './labV2HeaderBannerCarouselItem'

const BANNER_DISMISSAL_PERIOD_DAYS = 7

export default function useLabV2HeaderBannerItems(): Array<PageBannerCarouselItem> {
  const uis = useAppSelector(state => state.system.features.labV2?.uis)
  const { pathname, currentRegionCode } = useLabRouteRequestContext()
  const { getCookie } = useContext(CookiesContext)
  const isSearchPage = useIsSearchPage()
  const isOfferPage = useIsOfferPage()
  const shouldShowTakeoverBanner = useShouldShowBanner({
    location: getTakeoverBannerLocation(isSearchPage, isOfferPage),
  })
  const [dismissedBannerIds, setDismissedBannerIds] = useState<Set<string>>(
    () => dismissedBannerIdsFromCookie(getCookie(LAB_V2_HEADER_BANNER_DISMISSALS_COOKIE)),
  )

  const refreshDismissedBannerIds = useCallback(() => {
    const next = dismissedBannerIdsFromCookie(getCookie(LAB_V2_HEADER_BANNER_DISMISSALS_COOKIE))
    setDismissedBannerIds(current => {
      if (current.size === next.size && [...next].every(id => current.has(id))) {
        return current
      }
      return next
    })
  }, [getCookie])

  const dismissBanner = useCallback((bannerId: string) => {
    const endDate = moment().add(BANNER_DISMISSAL_PERIOD_DAYS, 'days').toISOString()
    recordLabV2HeaderBannerDismissal(bannerId, endDate)
    refreshDismissedBannerIds()
  }, [refreshDismissedBannerIds])

  const banners = useMemo(
    () => headerBannersForPage(headerBannersFromUis(uis), pathname, dismissedBannerIds, currentRegionCode),
    [uis, pathname, dismissedBannerIds, currentRegionCode],
  )

  useEffect(() => {
    if (banners.length === 0) {
      return
    }

    const handleVisibilityChange = () => {
      if (!document.hidden) {
        refreshDismissedBannerIds()
      }
    }

    document.addEventListener('visibilitychange', handleVisibilityChange)
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange)
    }
  }, [banners.length, refreshDismissedBannerIds])

  return useMemo(() => {
    if (shouldShowTakeoverBanner) {
      return []
    }

    return banners.map((banner): PageBannerCarouselItem => {
      return labV2HeaderBannerCarouselItem(banner, () => dismissBanner(headerBannerDismissalId(banner)))
    })
  }, [
    shouldShowTakeoverBanner,
    banners,
    dismissBanner,
  ])
}
